'use client'

// PostHog initialization + pageview tracking for the App Router.
//
// Two changes from the original Provider-wrapper pattern:
//
// 1. Sibling, not wrapper. The previous version wrapped {children}
//    in a client-component Provider, which forced the entire app
//    tree under a client boundary and contributed to the homepage's
//    BAILOUT_TO_CLIENT_SIDE_RENDERING signal flagged by the SEO
//    perf audit. Codebase grep confirmed no component uses the
//    React context (usePostHog hook) — every consumer imports the
//    posthog-js singleton directly — so dropping the Provider is
//    behavior-preserving.
//
// 2. Dynamic import of posthog-js. The static import pulled the
//    ~183KB SDK into the critical-path bundle. Now the SDK loads
//    lazily after hydration, on first effect, via a cached
//    module-level promise.
//
// PostHog's JS singleton queues capture() calls until init() is
// flushed, so consumers (join-form, notmember-form, posthog-identify)
// that import posthog-js directly still record events even if their
// code runs before this component's effect hydrates.

import { Suspense, useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

const POSTHOG_KEY  = process.env.NEXT_PUBLIC_POSTHOG_KEY
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST

// Module-level cache so concurrent callers share one import + init.
let posthogPromise: Promise<typeof import('posthog-js').default> | null = null
let initialized = false

async function loadAndInit(): Promise<typeof import('posthog-js').default | null> {
  if (typeof window === 'undefined') return null
  if (!POSTHOG_KEY || !POSTHOG_HOST) return null

  if (!posthogPromise) {
    posthogPromise = import('posthog-js').then(m => m.default)
  }
  const posthog = await posthogPromise

  if (!initialized) {
    posthog.init(POSTHOG_KEY, {
      api_host:          POSTHOG_HOST,
      // POSTHOG_HOST is `/ingest` in prod (reverse-proxied via
      // next.config rewrites) so adblockers don't strip events.
      // ui_host pins the link targets in PostHog's "open in
      // dashboard" buttons back to the real PostHog UI, since
      // our own domain doesn't serve one.
      ui_host:           'https://us.posthog.com',
      capture_pageview:  false,
      capture_pageleave: true,
      autocapture:       true,
      // Profile every visitor — required for top-of-funnel
      // attribution (anon → /notmember or /join → lead → quote).
      person_profiles:   'always',
      session_recording: {
        // Mask <input>/<textarea> by default so we don't record
        // emails, phone numbers, or property addresses.
        maskAllInputs: true,
      },
    })
    initialized = true
  }
  return posthog
}

function PageViewTracker() {
  const pathname     = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    loadAndInit().then(ph => {
      if (!ph) return
      let url = window.location.origin + pathname
      const search = searchParams?.toString()
      if (search) url += '?' + search
      ph.capture('$pageview', { $current_url: url })
    })
  }, [pathname, searchParams])

  return null
}

// Default export keeps the existing layout.tsx import working. Now
// renders nothing visible — it's a side-effect-only sibling, not a
// wrapper around children.
export default function PostHogProvider() {
  if (!POSTHOG_KEY) return null
  // useSearchParams() must be inside Suspense per Next.js App Router
  // contract. Wrapping just the tracker keeps the bailout boundary
  // tight to the analytics subtree.
  return (
    <Suspense fallback={null}>
      <PageViewTracker />
    </Suspense>
  )
}
